import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth, usePerms } from "@/lib/auth";
import AppShell from "@/components/AppShell";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Orders from "@/pages/Orders";
import OrderDetail from "@/pages/OrderDetail";
import Inventory from "@/pages/Inventory";
import Accounts from "@/pages/Accounts";
import Mapping from "@/pages/Mapping";
import WarehousePage from "@/pages/WarehousePage";
import WarehouseDetail from "@/pages/WarehouseDetail";
import Bank from "@/pages/Bank";
import Reports from "@/pages/Reports";
import Imports from "@/pages/Imports";
import Users from "@/pages/Users";
import Teams from "@/pages/Teams";
import Settings from "@/pages/Settings";
import Logs from "@/pages/Logs";
import Customers from "@/pages/Customers";
import CustomerDetail from "@/pages/CustomerDetail";
import Suggestions from "@/pages/Suggestions";
import Procurement from "@/pages/Procurement";
import Inbound from "@/pages/Inbound";
import Picklist from "@/pages/Picklist";
import PriceUpdate from "@/pages/PriceUpdate";
import Chat from "@/pages/Chat";
import Listings from "@/pages/Listings";
import Downloads from "@/pages/Downloads";
import Sourcing1688 from "@/pages/Sourcing1688";
import MasterData from "@/pages/MasterData";
import AsinImages from "@/pages/AsinImages";
import Catalog from "@/pages/Catalog";
import AllocationHistory from "@/pages/AllocationHistory";
import LanguageShell from "@/components/LanguageShell";

function Protected({ children }) {
  const { user } = useAuth();
  if (user === null) return <div className="h-screen flex items-center justify-center text-slate-500">Loading…</div>;
  if (user === false) return <Navigate to="/login" replace />;
  return <AppShell>{children}</AppShell>;
}

// Sep 7 2026 — Dashboard is now permission-gated. Non-admin users without
// the `dashboard` view grant are redirected to the first module they can
// actually see (or to /login when they somehow have nothing).
const DASHBOARD_FALLBACK_ORDER = [
  ["orders", "/orders"], ["inventory", "/inventory"], ["customers", "/customers"],
  ["suggestions", "/suggestions"], ["procurement", "/procurement"], ["inbound", "/inbound"],
  ["picklist", "/picklist"], ["allocation_history", "/allocation-history"],
  ["price_update", "/price-update"], ["listings", "/listings"], ["sourcing1688", "/1688"],
  ["master", "/master"], ["asin_images", "/asin-images"], ["catalog", "/catalog"],
  ["chat", "/chat"], ["accounts", "/accounts"], ["mapping", "/mapping"],
  ["warehouse", "/warehouse"], ["bank", "/bank"], ["reports", "/reports"],
  ["imports", "/imports"], ["downloads", "/downloads"], ["users", "/users"],
  ["teams", "/teams"], ["settings", "/settings"], ["logs", "/logs"],
];

function DashboardGate({ children }) {
  const { canView, ready, isAdmin } = usePerms();
  if (!ready) return <div className="h-screen flex items-center justify-center text-slate-500">Loading…</div>;
  if (isAdmin || canView("dashboard")) return children;
  const fallback = DASHBOARD_FALLBACK_ORDER.find(([k]) => canView(k));
  return <Navigate to={fallback ? fallback[1] : "/login"} replace />;
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <LanguageShell />
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/" element={<Protected><DashboardGate><Dashboard /></DashboardGate></Protected>} />
          <Route path="/orders" element={<Protected><Orders /></Protected>} />
          <Route path="/orders/:id" element={<Protected><OrderDetail /></Protected>} />
          <Route path="/inventory" element={<Protected><Inventory /></Protected>} />
          <Route path="/accounts" element={<Protected><Accounts /></Protected>} />
          <Route path="/mapping" element={<Protected><Mapping /></Protected>} />
          <Route path="/warehouse" element={<Protected><WarehousePage /></Protected>} />
          <Route path="/warehouse/:id" element={<Protected><WarehouseDetail /></Protected>} />
          <Route path="/bank" element={<Protected><Bank /></Protected>} />
          <Route path="/reports" element={<Protected><Reports /></Protected>} />
          <Route path="/imports" element={<Protected><Imports /></Protected>} />
          <Route path="/users" element={<Protected><Users /></Protected>} />
          <Route path="/teams" element={<Protected><Teams /></Protected>} />
          <Route path="/settings" element={<Protected><Settings /></Protected>} />
          <Route path="/logs" element={<Protected><Logs /></Protected>} />
          <Route path="/customers" element={<Protected><Customers /></Protected>} />
          <Route path="/customers/:phone" element={<Protected><CustomerDetail /></Protected>} />
          <Route path="/suggestions" element={<Protected><Suggestions /></Protected>} />
          <Route path="/procurement" element={<Protected><Procurement /></Protected>} />
          <Route path="/inbound" element={<Protected><Inbound /></Protected>} />
          <Route path="/picklist" element={<Protected><Picklist /></Protected>} />
          <Route path="/price-update" element={<Protected><PriceUpdate /></Protected>} />
          <Route path="/chat" element={<Protected><Chat /></Protected>} />
          <Route path="/listings" element={<Protected><Listings /></Protected>} />
          <Route path="/downloads" element={<Protected><Downloads /></Protected>} />
          <Route path="/1688" element={<Protected><Sourcing1688 /></Protected>} />
          <Route path="/master" element={<Protected><MasterData /></Protected>} />
          <Route path="/asin-images" element={<Protected><AsinImages /></Protected>} />
          <Route path="/catalog" element={<Protected><Catalog /></Protected>} />
          <Route path="/allocation-history" element={<Protected><AllocationHistory /></Protected>} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <Toaster position="top-right" richColors closeButton />
      </BrowserRouter>
    </AuthProvider>
  );
}
